import { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { DraftDesignConfiguration } from '@/types';
import { t } from '@/utils/i18n';
import { STICKER_SIZE_ROUTE } from '@/hooks/useStickerSize';
import { selectConfigurations } from '@/redux/draft/getters/selectConfigurations';
import { StoreState } from '@/redux/types';
import { CaretRight } from '@/components/common/icons/Caret';
import { SubPanel } from '@/components/common/panel/panel';
import { StickerSizeSelector } from '@/components/desktop/design-configuration/StickerSizeSelector';
import type { ProductTypePanelComponent } from '@/components/desktop/product-info/ProductTypePanel';
import { getLocalizedSize } from '@/components/draft/utils/getLocalizedSize';
import './StickerPanel.scss';

interface StickerSizeProps {
  configuration?: DraftDesignConfiguration;
  trackDesignSizingPanel?: () => void;
}

export const StickerPanelAction: FunctionComponent<StickerSizeProps> = ({
  configuration,
  trackDesignSizingPanel,
}) => {
  if (!configuration) {
    return null;
  }

  const { scale, size, restrict } = configuration;

  if (restrict?.scale) return null;

  return (
    <Link to={STICKER_SIZE_ROUTE} className="sp-info-line" onClick={trackDesignSizingPanel}>
      <span>{t('stickerPanel.action')}</span>
      <span>
        {`${getLocalizedSize(size.width * scale)} x ${getLocalizedSize(size.height * scale)}`}
        <CaretRight className="sp-info-line__icon" />
      </span>
    </Link>
  );
};

export const StickerSizePanel: FunctionComponent<StickerSizeProps> = ({ configuration }) => {
  const navigate = useNavigate();
  const isActive = Boolean(useMatch(STICKER_SIZE_ROUTE));

  useEffect(() => {
    if (!configuration && isActive) {
      navigate('/');
    }
    // navigate is not stable but should not trigger the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, isActive]);

  if (!configuration || configuration?.restrict?.scale) {
    return null;
  }

  return (
    <SubPanel
      active={isActive}
      headline={t('stickerPanel.action')}
      parent={t('productInfoPanel.label')}
      onBack={() => navigate('/')}
    >
      <StickerSizeSelector />
    </SubPanel>
  );
};

interface StickerPanelProps {
  Panel: ProductTypePanelComponent;
}

export const StickerPanel: FunctionComponent<StickerPanelProps> = ({ Panel }) => {
  const configuration = useSelector(
    (state: StoreState) => selectConfigurations(state)[0] as DraftDesignConfiguration | undefined
  );

  return (
    <Panel toggle={<StickerPanelAction configuration={configuration} />}>
      <StickerSizePanel configuration={configuration} />
    </Panel>
  );
};
